import { useMemo } from 'react';

type UsePopupOffsetParams = {
  width: number;
  height: number;
  placement: 'top' | 'bottom';
  shouldShowPopupLeft: boolean;
  screenWidth: number;
  isMobile?: boolean;
};

export const usePopupOffset = ({ width, height, isMobile, placement, shouldShowPopupLeft, screenWidth }: UsePopupOffsetParams): [number, number] => {
  return useMemo(() => {
    const isBottomPlacement = placement === 'bottom';

    if (!isMobile) {
      return placement === 'bottom' ? [12, height - 17] : [12, 0];
    }

    // Mobile device - use positioning logic
    const isFieldNarrow = width <= 40;
    const isFieldShort = height < 40;
    const isFieldTall = height > 40;

    const getDeviceSize = () => {
      if (screenWidth <= 430) {
        return 'xsmall-device';
      }
      if (screenWidth <= 768) {
        return 'small-device';
      }
      if (screenWidth <= 834) {
        return 'medium-device';
      }
      if (screenWidth <= 1024) {
        return 'large-device';
      }
      return 'xlarge-device';
    };

    const deviceSize = getDeviceSize();

    if (shouldShowPopupLeft) {
      // Left-side popup positioning - mobile device-specific settings
      let widthMultiplier, topHeightOffset;

      switch (deviceSize) {
        case 'xsmall-device':
          widthMultiplier = isFieldNarrow ? 0.75 : 0.1;
          topHeightOffset = isFieldTall ? 0.72 : isFieldShort ? 1 : 0.75;
          break;

        case 'small-device':
          widthMultiplier = isFieldNarrow ? 0.95 : 0.1;
          topHeightOffset = isFieldTall ? 0.45 : isFieldShort ? 1 : 0.75;
          break;

        case 'medium-device':
          widthMultiplier = isFieldNarrow ? 0.8 : 0.08;
          topHeightOffset = isFieldTall ? 0.42 : 0.7;
          break;

        case 'large-device':
          widthMultiplier = isFieldNarrow ? 1 : 0.06;
          topHeightOffset = isFieldTall ? 0.28 : isFieldShort ? 1 : 0.6;
          break;

        case 'xlarge-device':
        default:
          widthMultiplier = isFieldNarrow ? 0.1 : 0.05;
          topHeightOffset = isFieldTall ? 0.3 : 0.5;
          break;
      }

      const widthOffset = -(width * widthMultiplier);
      const heightOffset = isBottomPlacement ? height - height * topHeightOffset : 0;

      return [widthOffset, heightOffset];
    } else {
      // Right-side popup positioning - mobile device-specific values
      let topHeightOffset;

      switch (deviceSize) {
        case 'xsmall-device':
          topHeightOffset = isFieldTall ? 0.72 : isFieldShort ? 1 : 0.75;
          break;

        case 'small-device':
          topHeightOffset = isFieldTall ? 0.45 : 0.7;
          break;

        case 'medium-device':
          topHeightOffset = isFieldTall ? 0.42 : 0.7;
          break;

        case 'large-device':
          topHeightOffset = isFieldTall ? 0.28 : 0.6;
          break;

        case 'xlarge-device':
        default:
          topHeightOffset = isFieldTall ? 0.3 : 0.5;
          break;
      }

      const widthOffset = 16;
      const heightOffset = isBottomPlacement ? height - height * topHeightOffset : 0;
      return [widthOffset, heightOffset];
    }
  }, [width, height, placement, shouldShowPopupLeft, screenWidth]);
};
